import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Loader2, Calendar, Briefcase, Mail, MapPin, Sparkles, Ticket, ShieldCheck, BadgeCheck, ExternalLink, Users,
} from 'lucide-react';
import ReferralWidget from '@/components/ReferralWidget';
import FollowersWidget from '@/components/FollowersWidget';

const StatusPill = ({ status }) => {
  const colors = {
    pending: 'bg-amber-400/15 text-amber-300',
    accepted: 'bg-emerald-400/15 text-emerald-300',
    declined: 'bg-rose-400/15 text-rose-300',
    completed: 'bg-cyan-400/15 text-cyan-300',
    open: 'bg-cyan-400/15 text-cyan-300',
    paid: 'bg-emerald-400/15 text-emerald-300',
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${colors[status] || 'bg-white/10 text-slate-300'}`}>{status}</span>;
};

const BookingRow = ({ b, mode, onChange }) => {
  const updateStatus = async (status) => {
    const fd = new FormData(); fd.append('status', status);
    await api.patch(`/bookings/${b.booking_id}/status`, fd);
    onChange?.();
  };
  return (
    <div className="glass rounded-2xl p-5 border border-white/10 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <Briefcase className="h-4 w-4 text-fuchsia-300" />
          <span className="text-white font-medium">{b.event_type}</span>
          <StatusPill status={b.status} />
        </div>
        <div className="text-xs text-slate-400 flex flex-wrap gap-3">
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {b.event_date}</span>
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {b.location}</span>
          <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {b.client_email}</span>
          {b.budget && <span>Budget: {b.budget}</span>}
        </div>
        <p className="text-sm text-slate-300 mt-2">{b.message}</p>
      </div>
      {mode === 'received' && b.status === 'pending' && (
        <div className="flex gap-2">
          <button onClick={() => updateStatus('accepted')} className="btn-ai text-white text-sm rounded-full px-4 py-2">Accept</button>
          <button onClick={() => updateStatus('declined')} className="text-rose-300 text-sm rounded-full px-4 py-2 border border-rose-500/30 hover:bg-rose-500/10">Decline</button>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState({ sent: [], received: [] });
  const [artist, setArtist] = useState(null);
  const [castings, setCastings] = useState([]);
  const [events, setEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p, c, e, t, k] = await Promise.all([
        api.get('/bookings/mine'),
        api.get('/artists/me/profile'),
        api.get('/castings/mine/list'),
        api.get('/events/mine/list'),
        api.get('/tickets/mine'),
        api.get('/kyc/me'),
      ]);
      setBookings(b.data);
      setArtist(p.data.artist);
      setCastings(c.data.castings);
      setEvents(e.data.events);
      setTickets(t.data.tickets);
      setKyc(k.data.kyc);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="pt-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div>;

  const hasPostings = castings.length > 0 || events.length > 0;

  return (
    <div className="pt-28 pb-24 max-w-6xl mx-auto px-6 sm:px-12">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 mb-3">Hello {user.name}</div>
          <h1 className="font-display text-4xl font-bold text-white tracking-tight">Your <span className="rainbow-text">universe</span></h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/post/casting" className="glass-strong hover:border-fuchsia-400/40 transition rounded-full px-5 py-2.5 text-white text-sm border border-white/10 flex items-center gap-2"><Briefcase className="h-4 w-4" /> Post casting</Link>
          <Link to="/post/event" className="glass-strong hover:border-fuchsia-400/40 transition rounded-full px-5 py-2.5 text-white text-sm border border-white/10 flex items-center gap-2"><Calendar className="h-4 w-4" /> Post event</Link>
          <Link to="/onboarding" className="btn-ai rounded-full px-5 py-2.5 text-white text-sm flex items-center gap-2"><Sparkles className="h-4 w-4" /> {artist ? 'Edit artist' : 'Become artist'}</Link>
        </div>
      </div>

      <ReferralWidget />

      {artist && <FollowersWidget artist={artist} />}

      {artist && (
        <div className="glass-strong rounded-2xl p-6 border border-white/10 mb-8 flex items-center gap-5">          <img src={artist.profile_image} alt="" className="h-16 w-16 rounded-xl object-cover" />
          <div className="flex-1">
            <div className="text-white font-display text-xl flex items-center gap-2">{artist.stage_name} <BadgeCheck className="h-4 w-4 text-cyan-300" /></div>
            <div className="text-xs text-slate-400 capitalize">{artist.verified_level} · {(artist.categories || []).join(' · ')} · {artist.location_city}</div>
          </div>
          <Link to={`/artists/${artist.artist_id}`} className="text-sm text-cyan-300 hover:text-cyan-200">Public profile →</Link>
          <Link to="/kyc" className="text-sm text-emerald-300 hover:text-emerald-200 flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> KYC {kyc?.status || 'not started'}</Link>
          <Link to="/pricing" className="btn-rainbow text-white text-sm rounded-full px-4 py-2">Go premium</Link>
        </div>
      )}

      <Tabs defaultValue="bookings" className="space-y-6">
        <TabsList className="glass-strong border border-white/10 bg-transparent gap-1 p-1 flex-wrap h-auto">
          <TabsTrigger value="bookings" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2">Bookings</TabsTrigger>
          {hasPostings && <TabsTrigger value="postings" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2">My postings</TabsTrigger>}
          <TabsTrigger value="tickets" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2">My tickets</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="space-y-8">
          <div>
            <h2 className="font-display text-2xl text-white mb-4">Requests received</h2>
            {bookings.received.length === 0 ? (
              <div className="text-slate-400 text-sm glass rounded-2xl p-6 border border-white/10">No booking requests yet.</div>
            ) : (
              <div className="space-y-3">{bookings.received.map((b) => <BookingRow key={b.booking_id} b={b} mode="received" onChange={load} />)}</div>
            )}
          </div>
          <div>
            <h2 className="font-display text-2xl text-white mb-4">Inquiries sent</h2>
            {bookings.sent.length === 0 ? (
              <div className="text-slate-400 text-sm glass rounded-2xl p-6 border border-white/10">No bookings sent yet. <Link to="/artists" className="text-fuchsia-300">Discover talent →</Link></div>
            ) : (
              <div className="space-y-3">{bookings.sent.map((b) => <BookingRow key={b.booking_id} b={b} mode="sent" />)}</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="postings" className="space-y-8">
          <div>
            <h2 className="font-display text-2xl text-white mb-4 flex items-center gap-2"><Briefcase className="h-5 w-5 text-emerald-300" /> Castings posted</h2>
            {castings.length === 0 ? (
              <div className="text-slate-400 text-sm glass rounded-2xl p-6 border border-white/10">No castings posted. <Link to="/post/casting" className="text-fuchsia-300">Post your first →</Link></div>
            ) : (
              <div className="space-y-3">
                {castings.map((c) => (
                  <div key={c.casting_id} className="glass rounded-2xl p-5 border border-white/10 flex items-center gap-4">
                    <div className="flex-1">
                      <Link to={`/castings/${c.casting_id}`} className="text-white font-medium hover:text-fuchsia-200">{c.title}</Link>
                      <div className="text-xs text-slate-400 mt-1">{c.role_type} · {c.location} · deadline {c.deadline}</div>
                    </div>
                    <div className="text-center">
                      <div className="font-display text-xl font-bold ai-text">{c.application_count || 0}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">applicants</div>
                    </div>
                    <Link to={`/castings/${c.casting_id}`} className="text-cyan-300 hover:text-cyan-200"><ExternalLink className="h-4 w-4" /></Link>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <h2 className="font-display text-2xl text-white mb-4 flex items-center gap-2"><Calendar className="h-5 w-5 text-cyan-300" /> Events posted</h2>
            {events.length === 0 ? (
              <div className="text-slate-400 text-sm glass rounded-2xl p-6 border border-white/10">No events posted. <Link to="/post/event" className="text-fuchsia-300">Publish one →</Link></div>
            ) : (
              <div className="space-y-3">
                {events.map((e) => (
                  <div key={e.event_id} className="glass rounded-2xl p-5 border border-white/10 flex items-center gap-4">
                    <div className="flex-1">
                      <Link to={`/events/${e.event_id}`} className="text-white font-medium hover:text-fuchsia-200">{e.title}</Link>
                      <div className="text-xs text-slate-400 mt-1">{e.city} · {e.event_date} · ₹{e.ticket_price || 0}</div>
                    </div>
                    <div className="text-center">
                      <div className="font-display text-xl font-bold rainbow-text flex items-center gap-1"><Users className="h-4 w-4 text-fuchsia-300" />{e.tickets_sold || 0}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">sold</div>
                    </div>
                    <Link to={`/events/${e.event_id}`} className="text-cyan-300 hover:text-cyan-200"><ExternalLink className="h-4 w-4" /></Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tickets">
          <h2 className="font-display text-2xl text-white mb-4">Your tickets</h2>
          {tickets.length === 0 ? (
            <div className="text-slate-400 text-sm glass rounded-2xl p-6 border border-white/10">No tickets yet. <Link to="/events" className="text-fuchsia-300">Browse events →</Link></div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {tickets.map((t) => (
                <div key={t.ticket_id} className="glass-strong rounded-2xl p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-widest text-cyan-300 flex items-center gap-1"><Ticket className="h-3 w-3" /> Ticket</span>
                    <StatusPill status={t.status} />
                  </div>
                  <div className="text-white font-display text-lg mb-1 truncate">{t.event?.title || 'Event'}</div>
                  <div className="text-xs text-slate-400 mb-3">{t.event?.event_date} · {t.event?.venue}, {t.event?.city}</div>
                  <div className="font-mono text-xs tracking-widest text-fuchsia-300 break-all bg-white/5 rounded-lg px-2 py-1">{t.qr_code}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
